import { useLoggedInUser, useUser } from '@tcc/shared-data-lib';
import { useMemo } from 'react';
import { useUserLoggedInConfigs } from '../utils/sharedData';
import { isDealerImpersonatingFarmer, isImpersonating, isImpersonationNotAvailable, isProxyingItself } from '../utils/isImpersonating';
import { ImpersonationMode } from '../types/impersonationModes';
import { queryParam } from '../utils/queryString';
import { User } from '../types/user';

export type UseImpersonationMode = {
  isLoading: boolean;
  impersonationModes: ImpersonationMode[];
  user?: User;
  impersonatedUser?: User;
  isImpersonating: boolean;
};

export const useImpersonationMode = (): UseImpersonationMode => {
  const { user, isLoading: isUseLoggedInUserLoading } = useLoggedInUser(useUserLoggedInConfigs);
  const impersonatedUserId = queryParam('client-id');
  const loggedInUserId = user?.id?.toString() || '';
  const userId = impersonatedUserId || loggedInUserId;
  const { user: impersonatedUser, isLoading: isUseUserLoading } = useUser({
    config: { skip: !user || !impersonatedUserId || loggedInUserId === impersonatedUserId },
    params: { legacyUserId: userId },
  });

  const impersonationModes = useMemo(() => {
    const impersonationModesSet: Set<ImpersonationMode> = new Set();
    if (!isUseLoggedInUserLoading && !isUseUserLoading && user) {
      if (isImpersonationNotAvailable(user.details)) impersonationModesSet.add(ImpersonationMode.IMPERSONATION_NOT_AVAILABLE);
      if (isImpersonating(user.details)) impersonationModesSet.add(ImpersonationMode.IMPERSONATION_ACTIVE);
      if (isProxyingItself(user.details)) impersonationModesSet.add(ImpersonationMode.PROXYING_ITSELF).add(ImpersonationMode.IMPERSONATION_ACTIVE);
      if (isDealerImpersonatingFarmer(user, impersonatedUser)) impersonationModesSet.add(ImpersonationMode.DEALER_PROXYING_FARMER);
    }

    return Array.from(impersonationModesSet);
  }, [
    isUseLoggedInUserLoading,
    isUseUserLoading,
    user,
    impersonatedUser
  ]);

  const inpersonationActive = impersonationModes.includes(ImpersonationMode.IMPERSONATION_ACTIVE) &&
    !impersonationModes.includes(ImpersonationMode.PROXYING_ITSELF);

  return {
    isLoading: isUseLoggedInUserLoading || isUseUserLoading,
    impersonationModes,
    user,
    impersonatedUser: impersonatedUser || user,
    isImpersonating: inpersonationActive
  };
};
